import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, Eye, ToggleLeft, ToggleRight, AlertCircle,
  CheckCircle, Clock, Upload, ArrowRight, BarChart2, QrCode, Sparkles, MapPin, List, RefreshCw,
  Printer, DollarSign, Brain, Download, Trophy
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import GrowthNetworkPage from './GrowthNetworkPage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CATEGORIES = ['Food & Drink', 'Nightlife', 'Fitness & Wellness', 'Activities', 'Free Deals', 'Convenience Stores', 'Liquor Stores'];

const DEFAULT_FORM = {
  title: '', description: '', category: 'Food & Drink',
  price: '', original_price: '', discount_text: '', image_url: '',
  start_date: '', end_date: '', city_id: '', tags: '',
  is_promoted: false,
};

function DealSalesRow({ deal, salesDeal, onUpdate }) {
  const [localVal, setLocalVal] = useState(salesDeal?.avg_sale_value?.toString() || deal?.avg_sale_value?.toString() || '');
  return (
    <div className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="font-semibold text-sm text-[#2B2B2B]">{deal.title || salesDeal?.title}</p>
          <p className="text-xs text-[#8A9A8B] mt-0.5">
            {salesDeal?.claim_count || deal.claim_count || 0} claims · {salesDeal?.redemption_count || 0} redeemed
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8A9A8B]">$</span>
            <input
              type="number" min="0" step="0.01"
              value={localVal}
              onChange={(e) => setLocalVal(e.target.value)}
              onBlur={() => onUpdate(deal.deal_id, localVal)}
              placeholder="avg sale"
              className="perkly-input pl-7 w-32 text-sm py-2"
              data-testid={`sales-input-${deal.deal_id}`}
            />
          </div>
          <div className="text-center text-xs">
            <p className="text-[#C8A97E] font-bold">${salesDeal?.est_claimed_sales?.toFixed(2) || '0.00'}</p>
            <p className="text-[#8A9A8B]">claimed</p>
          </div>
          <div className="text-center text-xs">
            <p className="text-[#5DBB63] font-bold">${salesDeal?.est_redeemed_sales?.toFixed(2) || '0.00'}</p>
            <p className="text-[#8A9A8B]">redeemed</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BusinessDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [deals, setDeals] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [dashTab, setDashTab] = useState('deals'); // deals | pins | verify | sales | analytics | ai
  const [showForm, setShowForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [subscription, setSubscription] = useState(null);
  const [promoStatus, setPromoStatus] = useState({ max_promoted: 0, current_promoted: 0, can_promote: false });

  // Verify claim state
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Sales state
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);

  // AI state
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  // PIN sheet state
  const [pinSheet, setPinSheet] = useState(null);
  const [pinSheetLoading, setPinSheetLoading] = useState(false);

  // Today's PINs state
  const [todayPins, setTodayPins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const pinsIntervalRef = useRef(null);

  // Store hours state
  const DAYS_OF_WEEK = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const DAY_LABELS = { monday:'Monday', tuesday:'Tuesday', wednesday:'Wednesday', thursday:'Thursday', friday:'Friday', saturday:'Saturday', sunday:'Sunday' };
  const DEFAULT_HOURS = Object.fromEntries(DAYS_OF_WEEK.map(d => [d, { open: '', close: '', closed: false }]));
  const [storeHours, setStoreHours] = useState(DEFAULT_HOURS);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursMsg, setHoursMsg] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/businesses/me`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
      fetch(`${API}/cities`).then((r) => r.json()),
      fetch(`${API}/businesses/subscription`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
      fetch(`${API}/businesses/promotion-status`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
    ]).then(([biz, citiesData, sub, promo]) => {
      setBusiness(biz);
      setCities(citiesData || []);
      setSubscription(sub?.subscription_id ? sub : null);
      if (promo) setPromoStatus(promo);
      if (biz) setForm((f) => ({ ...f, city_id: biz.city_id, end_date: sub?.end_date || '' }));
      setLoadingBusiness(false);
    }).catch(() => setLoadingBusiness(false));
  }, []);

  useEffect(() => {
    if (business?.status === 'approved') {
      fetch(`${API}/businesses/deals`, { credentials: 'include' })
        .then((r) => r.json())
        .then((data) => setDeals(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, [business]);

  const openCreateForm = () => {
    setEditingDeal(null);
    setForm({ ...DEFAULT_FORM, city_id: business?.city_id || '' });
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const openEditForm = (deal) => {
    setEditingDeal(deal);
    setForm({
      title: deal.title, description: deal.description,
      category: deal.category, price: deal.price ?? '',
      original_price: deal.original_price ?? '', discount_text: deal.discount_text || '',
      image_url: deal.image_url, start_date: deal.start_date || '',
      end_date: deal.end_date || '', city_id: deal.city_id,
      tags: (deal.tags || []).join(', '),
      is_promoted: deal.is_promoted || false,
    });
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSuccess('');
    try {
      const payload = {
        ...form,
        price: form.price !== '' ? parseFloat(form.price) : null,
        original_price: form.original_price !== '' ? parseFloat(form.original_price) : null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };

      const url = editingDeal ? `${API}/businesses/deals/${editingDeal.deal_id}` : `${API}/businesses/deals`;
      const method = editingDeal ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');

      setFormSuccess(editingDeal ? 'Deal updated!' : 'Deal created!');
      setShowForm(false);
      const dealsRes = await fetch(`${API}/businesses/deals`, { credentials: 'include' });
      setDeals(Array.isArray(await dealsRes.json()) ? await (await fetch(`${API}/businesses/deals`, { credentials: 'include' })).json() : []);
    } catch (err) {
      setFormError(err.message);
    }
    setFormLoading(false);
  };

  const handleVerifyClaim = async (e) => {
    e.preventDefault();
    if (!verifyCode.trim()) return;
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const res = await fetch(`${API}/businesses/verify-claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ claim_code: verifyCode.trim().toUpperCase() }),
      });
      setVerifyResult(await res.json());
    } catch { setVerifyResult({ valid: false, reason: 'error', message: 'Network error. Please retry.' }); }
    setVerifyLoading(false);
  };

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadAnalytics = async () => {
    if (analytics) return;
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API}/businesses/analytics`, { credentials: 'include' });
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { /* silent */ }
    setAnalyticsLoading(false);
  };

  const loadTodayPins = useCallback(async () => {
    setPinsLoading(true);
    try {
      const [pinsRes, actRes] = await Promise.all([
        fetch(`${API}/businesses/today-pins`, { credentials: 'include' }),
        fetch(`${API}/businesses/recent-activity`, { credentials: 'include' }),
      ]);
      if (pinsRes.ok) setTodayPins(await pinsRes.json());
      if (actRes.ok) setRecentActivity(await actRes.json());
    } catch (e) { /* silent */ }
    setPinsLoading(false);
  }, []);

  const loadPinSheet = useCallback(async () => {
    setPinSheetLoading(true);
    try {
      const res = await fetch(`${API}/businesses/pin-sheet`, { credentials: 'include' });
      if (res.ok) setPinSheet(await res.json());
    } catch (e) { /* silent */ }
    setPinSheetLoading(false);
  }, []);

  const loadSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const res = await fetch(`${API}/businesses/sales-summary`, { credentials: 'include' });
      if (res.ok) setSalesData(await res.json());
    } catch (e) { /* silent */ }
    setSalesLoading(false);
  }, []);

  const loadHours = useCallback(async () => {
    if (!business?.business_id) return;
    setHoursLoading(true);
    try {
      const res = await fetch(`${API}/businesses/${business.business_id}/store-hours`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStoreHours({ ...DEFAULT_HOURS, ...data.store_hours });
      }
    } catch (e) { /* silent */ }
    setHoursLoading(false);
  }, [business?.business_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveHours = async () => {
    if (!business?.business_id) return;
    setHoursSaving(true);
    setHoursMsg('');
    try {
      const res = await fetch(`${API}/businesses/${business.business_id}/store-hours`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_hours: storeHours }),
      });
      if (res.ok) {
        setHoursMsg('Store hours saved!');
        setTimeout(() => setHoursMsg(''), 3000);
      }
    } catch (e) { setHoursMsg('Failed to save hours'); }
    setHoursSaving(false);
  };

  const loadAiSuggestions = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const res = await fetch(`${API}/businesses/ai-suggestions`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setAiSuggestions(data.suggestions);
    } catch (err) { setAiError(err.message); }
    setAiLoading(false);
  };

  const handleRegeneratePins = async () => {
    if (!window.confirm('Regenerate PINs? Current PINs will immediately become invalid.')) return;
    try {
      const res = await fetch(`${API}/businesses/pin-sheet/regenerate`, { method: 'POST', credentials: 'include' });
      if (res.ok) { await loadPinSheet(); await loadTodayPins(); }
    } catch (e) { /* silent */ }
  };

  const printPinSheet = () => {
    if (!pinSheet?.has_schedule || !pinSheet.weeks?.length) {
      alert('No PIN schedule available. Please activate a subscription first.');
      return;
    }

    const planLabel = (pinSheet.plan_type || '')
      .replace('monthly', 'Monthly Growth')
      .replace('campaign', 'Campaign Boost')
      .replace('weekly', 'Weekly Boost')
      .replace('trial', 'Free Trial');

    const weeksHTML = pinSheet.weeks.map(week => {
      const isActiveWeek = week.days?.some(d => d.is_today || d.is_valid_now);
      const dayRows = (week.days || []).flatMap(day => {
        const isToday = day.is_today || day.is_valid_now;
        const cls = isToday ? 'today' : (day.is_past ? 'past' : '');
        return (day.deal_pins || []).map((dp, di) => `
          <tr class="${cls}">
            ${di === 0 ? `<td rowspan="${day.deal_pins.length}" class="daycol">${day.day_of_week?.slice(0,3)}${isToday ? ' <span class="dot">●</span>' : ''}</td>
                         <td rowspan="${day.deal_pins.length}" class="datecol">${day.date?.slice(5) || ''}</td>` : ''}
            <td class="dealcol">${dp.deal_title || ''}</td>
            <td class="lettercol">${dp.deal_letter || ''}</td>
            <td class="pincol">${dp.full_pin_code || ''}</td>
          </tr>`);
      }).join('');

      return `
        <div class="week${isActiveWeek ? ' active' : ''}">
          <div class="wk-head">
            <span>Week ${week.week_number}${isActiveWeek ? ' &mdash; Active Now' : ''}</span>
            <span class="wk-dates">${week.start_date || ''} &rarr; ${week.end_date || ''}</span>
          </div>
          <table>
            <thead><tr><th>Day</th><th>Date</th><th>Deal</th><th>Ltr</th><th>PIN</th></tr></thead>
            <tbody>${dayRows}</tbody>
          </table>
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${business?.name || 'Business'} &mdash; Perkly PIN Sheet</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,sans-serif;font-size:11px;color:#2B2B2B;background:white;padding:14mm;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    @page{size:A4 portrait;margin:10mm;}
    .no-print{text-align:center;padding:0 0 18px;}
    button{background:#2B2B2B;color:white;border:none;padding:9px 22px;border-radius:20px;cursor:pointer;font-size:13px;font-weight:700;}
    .hdr{text-align:center;border-bottom:2px solid #C8A97E;padding-bottom:12px;margin-bottom:18px;}
    .brand{font-size:20px;font-weight:800;letter-spacing:-0.01em;}
    .brand span{display:inline-block;width:7px;height:7px;background:#C8A97E;border-radius:50%;margin-left:3px;vertical-align:middle;}
    .title{font-size:14px;font-weight:700;margin:4px 0 2px;}
    .sub{font-size:9.5px;color:#8A9A8B;}
    .week{margin-bottom:18px;page-break-inside:avoid;break-inside:avoid;}
    .wk-head{display:flex;justify-content:space-between;align-items:center;padding:6px 10px;background:#F6F1E9;border-radius:5px 5px 0 0;font-weight:700;font-size:10.5px;}
    .week.active .wk-head{background:#C8A97E;color:white;}
    .wk-dates{font-size:9px;font-weight:500;opacity:.8;}
    table{width:100%;border-collapse:collapse;border:1px solid #e5e0d8;border-top:none;}
    th{background:#F6F1E9;text-align:left;padding:5px 9px;font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#8A9A8B;border-bottom:1px solid #e5e0d8;}
    td{padding:6px 9px;border-bottom:1px solid #f0ebe1;vertical-align:middle;}
    tr:last-child td{border-bottom:none;}
    tr.today td{background:rgba(200,169,126,.09);font-weight:600;}
    tr.past td{opacity:.42;}
    .pincol{font-family:monospace;font-weight:800;font-size:13px;letter-spacing:.04em;color:#C8A97E;}
    .week.active .pincol{color:#2B2B2B;}
    .dot{color:#C8A97E;}
    .week.active .dot{color:white;}
    .footer{text-align:center;margin-top:18px;font-size:8.5px;color:#8A9A8B;}
    @media print{.no-print{display:none!important;}}
  </style>
</head>
<body>
  <div class="no-print">
    <button onclick="window.print()">&#128438; Print PIN Sheet</button>
  </div>
  <div class="hdr">
    <div class="brand">Perkly<span></span></div>
    <div class="title">${business?.name || 'Business'} &mdash; PIN Sheet</div>
    <div class="sub">${planLabel} &middot; ${pinSheet.start_date || ''} to ${pinSheet.end_date || ''} &middot; PINs change daily &middot; Keep at register</div>
  </div>
  ${weeksHTML}
  <div class="footer">Perkly &middot; ${business?.name || ''} &middot; useperkly.com &middot; PINs are unique per deal per day</div>
  <script>
    window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 450); });
  </script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const pw = window.open(url, '_blank', 'width=860,height=720,scrollbars=yes');
    if (!pw) { URL.revokeObjectURL(url); alert('Pop-up blocked. Please allow pop-ups for this site and try again.'); return; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const updateDealSalesValue = async (dealId, value) => {
    try {
      await fetch(`${API}/deals/${dealId}/sales-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ avg_sale_value: parseFloat(value) || null }),
      });
      await loadSales();
    } catch (e) { /* silent */ }
  };

  // Auto-refresh PINs panel every 30s when on deals tab
  useEffect(() => {
    if (dashTab !== 'deals' || business?.status !== 'approved') return;
    let cancelled = false;
    const fetchPins = async () => {
      if (cancelled) return;
      try {
        const [pinsRes, actRes] = await Promise.all([
          fetch(`${API}/businesses/today-pins`, { credentials: 'include' }),
          fetch(`${API}/businesses/recent-activity`, { credentials: 'include' }),
        ]);
        if (!cancelled) {
          if (pinsRes.ok) setTodayPins(await pinsRes.json());
          if (actRes.ok) setRecentActivity(await actRes.json());
        }
      } catch (e) { /* silent */ }
    };
    fetchPins();
    const id = setInterval(fetchPins, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dashTab, business?.status]);

  const handleDelete = async (dealId) => {
    try {
      await fetch(`${API}/businesses/deals/${dealId}`, { method: 'DELETE', credentials: 'include' });
      setDeals((prev) => prev.filter((d) => d.deal_id !== dealId));
      setDeleteConfirm(null);
    } catch (e) { /* delete failed */ }
  };

  const handleToggleActive = async (deal) => {
    try {
      await fetch(`${API}/businesses/deals/${deal.deal_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !deal.is_active }),
      });
      setDeals((prev) => prev.map((d) => d.deal_id === deal.deal_id ? { ...d, is_active: !d.is_active } : d));
    } catch (e) { /* toggle failed */ }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setFormError('Unsupported file type. Please use JPG, PNG, or WebP.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setFormError(`Image too large (${(file.size/1024/1024).toFixed(1)} MB). Maximum 15 MB.`);
      return;
    }
    setImageUploading(true);
    setUploadProgress(0);
    setFormError('');
    const CHUNK = 512 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK);
    const uploadId = `deal_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      let lastResult = null;
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append('file', new File([chunk], file.name, { type: file.type }));
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('total_chunks', String(totalChunks));
        const res = await fetch(`${API}/upload/image`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Upload failed');
        lastResult = data;
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      const imageUrl = lastResult.path
        ? `${process.env.REACT_APP_BACKEND_URL}${lastResult.path}`
        : lastResult.url;
      setForm((f) => ({ ...f, image_url: imageUrl }));
    } catch (err) {
      setFormError(err.message);
    }
    setImageUploading(false);
  };

  if (loadingBusiness) {
    return (
      <div className="min-h-screen bg-[#F6F1E9]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B] mb-1">Business Dashboard</p>
            <h1 className="text-3xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="dashboard-title">
              {business ? business.name : 'My Business'}
            </h1>
          </div>
          {business?.status === 'approved' && dashTab === 'deals' && (
            <button data-testid="add-deal-button" onClick={openCreateForm} className="btn-primary text-sm px-5 py-2.5">
              <Plus size={16} /> Add Deal
            </button>
          )}
        </div>

        {/* Dashboard Tabs */}
        {business?.status === 'approved' && (
          <div className="flex gap-1 bg-white rounded-2xl p-1.5 border border-[#8A9A8B]/10 mb-6 overflow-x-auto" data-testid="business-dashboard-tabs">
            {[
              { id: 'deals',     label: 'My Deals',    icon: Plus },
              { id: 'pins',      label: 'PIN Sheet',   icon: Printer },
              { id: 'sales',     label: 'Sales',       icon: DollarSign },
              { id: 'hours',     label: 'Hours',       icon: Clock },
              { id: 'analytics', label: 'Analytics',   icon: BarChart2 },
              { id: 'growth',    label: '🚀 Growth',   icon: Trophy },
              ...(subscription?.plan_type === 'monthly' || subscription?.plan_type === 'trial' ? [{ id: 'ai', label: 'AI Insights', icon: Brain }] : []),
            ].map(t => (
              <button
                key={t.id}
                onClick={() => {
                  setDashTab(t.id);
                  if (t.id === 'analytics') loadAnalytics();
                  if (t.id === 'pins') loadPinSheet();
                  if (t.id === 'sales') loadSales();
                  if (t.id === 'hours') loadHours();
                }}
                data-testid={`biz-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${dashTab === t.id ? 'bg-[#2B2B2B] text-[#F6F1E9]' : 'text-[#2B2B2B]/60 hover:text-[#2B2B2B]'}`}
              >
                <t.icon size={13} />
                {t.id === 'ai' ? <><span>AI</span><span className="bg-[#C8A97E] text-white text-xs px-1 rounded ml-0.5">Pro</span></> : t.label}
              </button>
            ))}
          </div>
        )}

        {/* Subscription / Trial Status Banner */}
        {(() => {
          const today = new Date();
          const trialEnd = business?.trial_end_date ? new Date(business.trial_end_date + 'T23:59:59') : null;
          const trialDaysLeft = trialEnd ? Math.ceil((trialEnd - today) / (1000 * 60 * 60 * 24)) : -1;
          const isTrial = trialDaysLeft > 0;
          const trialExpired = trialEnd && trialDaysLeft <= 0;

          if (subscription) {
            const end = new Date(subscription.end_date + 'T23:59:59');
            const daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
            const isUrgent = daysLeft <= 3;
            const isTrial = subscription.plan_type === 'trial';
            const planDays = subscription.plan_type === 'weekly' ? 7 : subscription.plan_type === 'trial' ? 60 : 30;
            const pct = Math.max(0, Math.min(100, (daysLeft / planDays) * 100));
            return (
              <div className={`rounded-2xl p-5 border mb-6 ${isUrgent && !isTrial ? 'bg-orange-50 border-orange-200' : 'bg-[#C8A97E]/8 border-[#C8A97E]/25'}`} data-testid="subscription-banner">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isUrgent && !isTrial ? 'bg-orange-100' : 'bg-[#C8A97E]/15'}`}>
                      <Clock size={18} className={isUrgent && !isTrial ? 'text-orange-500' : 'text-[#C8A97E]'} />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-[#2B2B2B] capitalize">
                        {isTrial ? 'Free Trial' : subscription.plan_type === 'weekly' ? 'Weekly Boost' : subscription.plan_type === 'monthly' ? 'Monthly Growth' : subscription.plan_type === 'biweekly' ? '2-Week Campaign' : 'Custom Campaign'} — Active
                      </p>
                      {isTrial ? (
                        <p className="text-xs text-[#2B2B2B]/60">4 deals · Map promotion · AI Insights · All monthly features included · {daysLeft <= 0 ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}</p>
                      ) : (
                        <p className="text-xs text-[#2B2B2B]/60">
                          Expires <strong>{new Date(subscription.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                          &nbsp;·&nbsp;<span className={isUrgent ? 'text-orange-600 font-bold' : ''}>{daysLeft <= 0 ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {!isTrial && (
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${isUrgent ? 'bg-orange-100 text-orange-600' : 'bg-[#C8A97E]/15 text-[#C8A97E]'}`}>
                        Max {subscription.max_deals} deal{subscription.max_deals > 1 ? 's' : ''}
                      </span>
                    )}
                    <div className="w-28 h-1.5 bg-[#8A9A8B]/20 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${isUrgent && !isTrial ? 'bg-orange-400' : 'bg-[#C8A97E]'}`} style={{ width: `${pct}%` }} />
                    </div>
                    {isTrial ? (
                      <Link to="/pricing" data-testid="upgrade-plan-btn" className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2B2B2B] text-[#F6F1E9] text-xs font-bold hover:bg-[#C8A97E] transition-colors">
                        Upgrade Plan <ArrowRight size={12} />
                      </Link>
                    ) : isUrgent ? (
                      <Link to="/pricing" className="text-xs font-bold text-orange-600 hover:text-orange-700 underline">Renew</Link>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          }

          if (isTrial) {
            return (
              <div className="rounded-2xl p-5 bg-[#C8A97E]/8 border border-[#C8A97E]/25 mb-6" data-testid="trial-active-banner">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#C8A97E]/15 flex items-center justify-center flex-shrink-0">
                      <Clock size={18} className="text-[#C8A97E]" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-[#2B2B2B]">Free Trial Active — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left</p>
                      <p className="text-xs text-[#2B2B2B]/60">4 deals · Map promotion · AI Insights · All monthly features included</p>
                    </div>
                  </div>
                  <Link to="/pricing" data-testid="upgrade-plan-btn" className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2B2B2B] text-[#F6F1E9] text-xs font-bold hover:bg-[#C8A97E] transition-colors">
                    Upgrade Plan <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            );
          }

          if (trialExpired) {
            return (
              <div className="rounded-2xl p-5 bg-red-50 border border-red-200 mb-6" data-testid="trial-expired-banner">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle size={18} className="text-red-500" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-red-700">Free Trial Ended</p>
                      <p className="text-xs text-red-600/80">Your deals have been paused. Purchase a plan to reactivate them and reach students.</p>
                    </div>
                  </div>
                  <Link to="/pricing" data-testid="purchase-plan-btn" className="flex-shrink-0 flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors">
                    Choose a Plan <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            );
          }

          return (
            <div className="rounded-2xl p-4 bg-[#F6F1E9] border border-[#8A9A8B]/20 mb-6 flex items-center gap-3" data-testid="no-subscription-banner">
              <Clock size={18} className="text-[#8A9A8B] flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-[#2B2B2B]">No active subscription</p>
                <p className="text-xs text-[#2B2B2B]/55">Contact admin or <Link to="/pricing" className="text-[#C8A97E] underline">purchase a plan</Link> to list deals.</p>
              </div>
            </div>
          );
        })()}

        {/* Business Status */}
        {!business ? (
          <div className="bg-white rounded-3xl p-10 text-center border border-[#8A9A8B]/10" data-testid="no-business-message">
            <AlertCircle size={40} className="text-[#C8A97E] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No Business Registered</h2>
            <p className="text-[#2B2B2B]/60 text-sm mb-6">Register your business to start adding deals and reaching customers.</p>
            <Link to="/business/register" className="btn-primary inline-flex">Register My Business</Link>
          </div>
        ) : business.status === 'pending' ? (
          <div className="bg-white rounded-3xl p-8 border border-[#C8A97E]/20" data-testid="business-pending-message">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C8A97E]/10 flex items-center justify-center flex-shrink-0">
                <Clock size={24} className="text-[#C8A97E]" />
              </div>
              <div>
                <h3 className="font-bold text-[#2B2B2B] mb-1">Pending Approval</h3>
                <p className="text-sm text-[#2B2B2B]/60">Your business <strong>{business.name}</strong> is under review. You&apos;ll be notified once approved (usually within 24 hours).</p>
              </div>
            </div>
          </div>
        ) : business.status === 'rejected' ? (
          <div className="bg-red-50 rounded-3xl p-8 border border-red-200" data-testid="business-rejected-message">
            <div className="flex items-center gap-4">
              <AlertCircle size={24} className="text-red-500 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-700 mb-1">Application Rejected</h3>
                <p className="text-sm text-red-600">Unfortunately, your business application was not approved. Contact support for more information.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Business Info Stats */}
            {dashTab === 'deals' && (
            <>
            {/* ── TODAY'S DEAL PINs ── */}
            {todayPins.length > 0 && (
              <div className="bg-[#2B2B2B] rounded-2xl p-5 mb-6" data-testid="today-pins-panel">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔑</span>
                    <div>
                      <h3 className="font-bold text-white text-sm">Today&apos;s Deal PINs</h3>
                      <p className="text-white/40 text-xs">Tell students these PINs &mdash; they enter it on their phone to redeem</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={loadTodayPins} className="text-white/40 hover:text-white transition-colors" title="Refresh">
                      <RefreshCw size={14} />
                    </button>
                    <span className="text-white/30 text-xs">Resets midnight</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {todayPins.map(p => (
                    <div key={p.deal_id} className="bg-white/8 rounded-2xl p-4 border border-white/10" data-testid={`pin-card-${p.deal_id}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <img src={p.image_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" onError={e => { e.target.style.display='none'; }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-xs line-clamp-1">{p.title}</p>
                          {p.discount_text && <p className="text-[#C8A97E] text-xs font-bold">{p.discount_text}</p>}
                        </div>
                      </div>
                      {/* PIN Display */}
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1.5" data-testid={`today-pin-${p.deal_id}`}>
                          {p.pin.split('').map((d, i) => (
                            <div key={i} className="w-11 h-13 bg-[#C8A97E] rounded-xl flex items-center justify-center text-white text-2xl font-black" style={{height:'52px'}}>
                              {d}
                            </div>
                          ))}
                        </div>
                        <div className="text-right">
                          <p className="text-[#C8A97E] text-xl font-bold">{p.redeemed_today}</p>
                          <p className="text-white/35 text-xs">redeemed</p>
                          <p className="text-white/25 text-xs">{p.claims_today} claimed</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Live activity feed */}
                {recentActivity.length > 0 && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2">Live Activity Today</p>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {recentActivity.slice(0, 8).map((evt) => (
                        <div key={`${evt.redeemed_at}-${evt.deal_id || evt.deal_title}`} className="flex items-center gap-2 text-xs text-white/55">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                          <span className="truncate">1 student redeemed <strong className="text-white/70">{evt.deal_title}</strong></span>
                          <span className="text-white/30 flex-shrink-0 ml-auto">
                            {new Date(evt.redeemed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { label: 'Total Deals', value: deals.length, color: '#C8A97E' },
                { label: 'Active Deals', value: deals.filter((d) => d.is_active).length, color: '#7BA05B' },
                { label: 'Total Views', value: deals.reduce((sum, d) => sum + (d.views || 0), 0), color: '#8A9A8B' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                  <p className="text-xs font-semibold text-[#2B2B2B]/50 uppercase tracking-wider mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold" style={{ color: stat.color, fontFamily: 'Playfair Display, serif' }}>{stat.value}</p>
                </div>
              ))}
            </div>
            </>
            )}
            {formSuccess && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl p-3 mb-4">
                <CheckCircle size={16} /> {formSuccess}
              </div>
            )}

            {/* DEALS TAB */}
            {dashTab === 'deals' && (
            <>
            {deals.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-[#8A9A8B]/10" data-testid="no-deals-dashboard">
                <p className="text-4xl mb-4">🎁</p>
                <h3 className="text-xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No Deals Yet</h3>
                <p className="text-[#2B2B2B]/60 text-sm mb-6">Create your first deal to start attracting customers.</p>
                <button onClick={openCreateForm} className="btn-primary inline-flex">
                  <Plus size={16} /> Create First Deal
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 overflow-hidden" data-testid="deals-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F6F1E9] border-b border-[#8A9A8B]/15">
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-6 py-4">Deal</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">Map</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">Category</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">Discount</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">Views</th>
                        <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">Status</th>
                        <th className="text-right text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-6 py-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#8A9A8B]/10">
                      {deals.map((deal) => (
                        <tr key={deal.deal_id} className="hover:bg-[#F6F1E9]/50 transition-colors" data-testid={`deal-row-${deal.deal_id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <img src={deal.image_url} alt={deal.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=100&q=60'; }} />
                              <span className="font-semibold text-sm text-[#2B2B2B] line-clamp-1">{deal.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {deal.is_promoted
                              ? <span className="text-xs font-bold text-[#C8A97E] flex items-center gap-1"><MapPin size={11} /> Promoted</span>
                              : <span className="text-xs text-[#2B2B2B]/35">Standard</span>}
                          </td>
                          <td className="px-4 py-4"><span className="text-xs bg-[#8A9A8B]/10 text-[#8A9A8B] px-2 py-1 rounded-full">{deal.category}</span></td>
                          <td className="px-4 py-4 text-sm text-[#2B2B2B]/60 flex items-center gap-1"><Eye size={13} /> {deal.views}</td>
                          <td className="px-4 py-4">
                            <button onClick={() => handleToggleActive(deal)} className="flex items-center gap-1.5" data-testid={`toggle-active-${deal.deal_id}`}>
                              {deal.is_active
                                ? <><ToggleRight size={20} className="text-green-500" /><span className="text-xs text-green-600">Active</span></>
                                : <><ToggleLeft size={20} className="text-[#8A9A8B]" /><span className="text-xs text-[#8A9A8B]">Inactive</span></>}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 justify-end">
                              <button onClick={() => openEditForm(deal)} className="p-2 rounded-lg hover:bg-[#C8A97E]/10 text-[#C8A97E] transition-colors" data-testid={`edit-deal-${deal.deal_id}`}><Edit2 size={15} /></button>
                              <button onClick={() => setDeleteConfirm(deal.deal_id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400 transition-colors" data-testid={`delete-deal-${deal.deal_id}`}><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}

            {dashTab === 'pins' && (
            <div data-testid="pin-sheet-panel">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Full PIN Sheet
                  </h2>
                  <p className="text-xs text-[#2B2B2B]/55 mt-0.5">
                    One unique base PIN per day. Deal letter stays constant (A, B, C…). Print and give to your cashier.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleRegeneratePins}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#8A9A8B]/30 text-xs font-semibold hover:border-[#C8A97E] transition-colors"
                  >
                    <RefreshCw size={13} /> Regenerate
                  </button>
                  <button
                    onClick={printPinSheet}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2B2B2B] text-[#F6F1E9] text-xs font-bold hover:bg-[#C8A97E] transition-colors"
                  >
                    <Printer size={13} /> Print Sheet
                  </button>
                </div>
              </div>

              {pinSheetLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !pinSheet?.has_schedule ? (
                <div className="bg-white rounded-2xl p-10 text-center border border-[#8A9A8B]/10">
                  <Printer size={32} className="text-[#C8A97E]/40 mx-auto mb-4" />
                  <h3 className="font-bold text-[#2B2B2B] mb-2">No PIN Schedule Yet</h3>
                  <p className="text-sm text-[#2B2B2B]/55">PIN schedules are auto-generated when your subscription is activated.</p>
                </div>
              ) : (
                <div className="space-y-6" id="pin-sheet-print">
                  {/* Print header */}
                  <div className="hidden print:block border-b border-gray-300 pb-4 mb-6 text-center">
                    <h1 className="text-xl font-bold text-gray-900">{business?.name} — Perkly PIN Sheet</h1>
                    <p className="text-sm text-gray-500 mt-1">
                      {pinSheet.plan_type?.replace('monthly','Monthly Growth').replace('campaign','Campaign Boost').replace('weekly','Weekly Boost').replace('trial','Free Trial')} ·
                      {' '}{pinSheet.start_date} to {pinSheet.end_date} · Keep at register. PINs change daily.
                    </p>
                  </div>

                  {/* COMPACT SINGLE-PAGE TABLE — all weeks, all days */}
                  {pinSheet.weeks.map((week) => {
                    const hasToday = week.days?.some((d) => d.is_today || d.is_valid_now);
                    return (
                      <div key={week.week_number}
                        className={`rounded-2xl overflow-hidden border-2 print:border print:border-gray-300 ${hasToday ? 'border-[#C8A97E]' : 'border-[#8A9A8B]/15'}`}
                        data-testid={`pin-week-${week.week_number}`}>
                        {/* Week header */}
                        <div className={`px-4 py-2.5 flex items-center justify-between print:bg-gray-100 ${hasToday ? 'bg-[#C8A97E]' : 'bg-[#F6F1E9]'}`}>
                          <p className={`font-bold text-sm ${hasToday ? 'text-white' : 'text-[#2B2B2B]'}`}>
                            Week {week.week_number}
                            {hasToday && <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">← Active Now</span>}
                          </p>
                          <p className={`text-xs ${hasToday ? 'text-white/75' : 'text-[#2B2B2B]/55'}`}>
                            {week.start_date} — {week.end_date}
                          </p>
                        </div>

                        {/* Compact table — one row per day */}
                        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                          <thead>
                            <tr className="bg-[#F6F1E9]/50 print:bg-gray-50">
                              <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-2">Day</th>
                              <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-2">Date</th>
                              <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-2">Deal</th>
                              <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-2">Letter</th>
                              <th className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-2">PIN</th>
                            </tr>
                          </thead>
                          <tbody>
                            {week.days?.map((day, di) => {
                              const isActive = day.is_today || day.is_valid_now;
                              const nDeals = day.deal_pins?.length || 1;
                              return day.deal_pins?.map((dp, dpi) => (
                                <tr key={`${day.date}-${dp.deal_id}`}
                                  className={`border-t border-[#8A9A8B]/10 print:border-gray-200 ${isActive ? 'bg-[#C8A97E]/5' : day.is_past ? 'opacity-45' : ''}`}>
                                  {dpi === 0 && (
                                    <>
                                      <td className="px-4 py-2 text-xs font-semibold text-[#2B2B2B]" rowSpan={nDeals}>
                                        {day.day_of_week?.slice(0,3)}
                                        {isActive && <span className="ml-1 text-[#C8A97E] text-xs">●</span>}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-[#2B2B2B]/60" rowSpan={nDeals}>{day.date?.slice(5)}</td>
                                    </>
                                  )}
                                  <td className="px-4 py-2 text-xs text-[#2B2B2B] max-w-[140px] truncate">{dp.deal_title}</td>
                                  <td className="px-4 py-2 text-xs font-bold text-[#8A9A8B]">{dp.deal_letter}</td>
                                  <td className="px-4 py-2">
                                    <span className={`font-mono font-bold text-sm ${isActive ? 'text-[#C8A97E]' : 'text-[#2B2B2B]'}`}
                                      data-testid={`pin-${dp.deal_id}-week-${week.week_number}`}>
                                      {dp.full_pin_code}
                                    </span>
                                  </td>
                                </tr>
                              ));
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}

                  <div className="text-center print:block">
                    <p className="text-xs text-[#2B2B2B]/35 print:text-gray-400">
                      Perkly · {business?.name} · Generated {new Date().toLocaleDateString()} ·
                      {' '}{pinSheet.total_days} days · PINs change every day · Same business same day = same base number
                    </p>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* SALES TAB */}
            {dashTab === 'sales' && (
            <div data-testid="sales-panel">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Sales Tracking</h2>
                  <p className="text-xs text-[#2B2B2B]/55 mt-0.5">Set average sale value per deal to track estimated revenue from Perkly claims.</p>
                </div>
              </div>
              {salesLoading ? (
                <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" /></div>
              ) : (
              <>
                {/* Totals */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10 text-center">
                    <p className="text-xs text-[#2B2B2B]/50 mb-1">Est. Claimed Sales</p>
                    <p className="text-2xl font-bold text-[#C8A97E]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      ${salesData?.totals?.est_claimed_sales?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-[#2B2B2B]/40 mt-0.5">from claim volume</p>
                  </div>
                  <div className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10 text-center">
                    <p className="text-xs text-[#2B2B2B]/50 mb-1">Est. Redeemed Sales</p>
                    <p className="text-2xl font-bold text-[#5DBB63]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      ${salesData?.totals?.est_redeemed_sales?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-[#2B2B2B]/40 mt-0.5">from verified redemptions</p>
                  </div>
                </div>

                {/* Per-deal sales config */}
                <div className="space-y-3">
                  {(salesData?.deals || deals).map((d) => (
                    <DealSalesRow
                      key={d.deal_id}
                      deal={d}
                      salesDeal={salesData?.deals?.find((s) => s.deal_id === d.deal_id) || d}
                      onUpdate={updateDealSalesValue}
                    />
                  ))}
                  {(!salesData?.deals || salesData.deals.length === 0) && (
                    <div className="bg-white rounded-2xl p-10 text-center border border-[#8A9A8B]/10">
                      <p className="text-[#2B2B2B]/40 text-sm">No deals yet. Add a deal to start tracking sales.</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-[#2B2B2B]/40 mt-4 text-center">Enter average sale value per deal to calculate estimated revenue from Perkly customers.</p>
              </>
              )}
            </div>
            )}

            {/* AI INSIGHTS TAB (Monthly Growth only) */}
            {dashTab === 'ai' && (
            <div data-testid="ai-insights-panel">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>AI Business Insights</h2>
                    <span className="text-xs bg-[#C8A97E] text-white font-bold px-2 py-0.5 rounded-full">Monthly Growth</span>
                  </div>
                  <p className="text-xs text-[#2B2B2B]/55">GPT-powered analysis of your deal performance with actionable recommendations.</p>
                </div>
                <button
                  onClick={loadAiSuggestions}
                  disabled={aiLoading}
                  className="btn-primary text-xs px-5 py-2.5 disabled:opacity-60"
                  data-testid="generate-ai-btn"
                >
                  {aiLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyzing...</> : <><Brain size={14} /> Generate Insights</>}
                </button>
              </div>
              {aiError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl p-4 mb-4">{aiError}</div>
              )}
              {!aiSuggestions && !aiLoading && !aiError && (
                <div className="bg-white rounded-2xl p-12 text-center border border-[#8A9A8B]/10">
                  <Brain size={36} className="text-[#C8A97E]/40 mx-auto mb-4" />
                  <h3 className="font-bold text-[#2B2B2B] mb-2">Ready to Analyze Your Business</h3>
                  <p className="text-sm text-[#2B2B2B]/55 mb-5">Click &ldquo;Generate Insights&rdquo; to get AI-powered recommendations based on your deal data.</p>
                  <button onClick={loadAiSuggestions} className="btn-primary inline-flex text-sm"><Brain size={14} /> Analyze My Business</button>
                </div>
              )}
              {aiSuggestions && (
                <div className="space-y-4">
                  {[
                    { key: 'campaign_summary', label: 'Campaign Summary', color: '#2B2B2B', bg: '#F6F1E9' },
                    { key: 'best_deal', label: 'Best Performing Deal', color: '#5DBB63', bg: '#EDF3E8' },
                    { key: 'weakest_deal', label: 'Weakest Deal', color: '#C87B7B', bg: '#F9EDEC' },
                    { key: 'deal_improvement', label: 'Deal Improvement', color: '#C8A97E', bg: '#FBF5EC' },
                    { key: 'best_time', label: 'Best Times to Run Deals', color: '#8A9A8B', bg: '#EEF1EE' },
                    { key: 'next_deal_suggestion', label: 'Suggested Next Deal', color: '#A07BC8', bg: '#F1EDF9' },
                    { key: 'growth_recommendation', label: 'Customer Growth', color: '#6B7CFF', bg: '#ECEFFE' },
                    { key: 'sales_improvement', label: 'Revenue Recommendation', color: '#C8A97E', bg: '#FBF5EC' },
                  ].map((item) => aiSuggestions[item.key] && (
                    <div key={item.key} className="rounded-2xl p-5 border" style={{ background: item.bg, borderColor: item.color + '25' }} data-testid={`ai-${item.key}`}>
                      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: item.color }}>{item.label}</p>
                      <p className="text-sm text-[#2B2B2B]/80 leading-relaxed">{aiSuggestions[item.key]}</p>
                    </div>
                  ))}
                  <p className="text-xs text-center text-[#2B2B2B]/35 mt-2">AI analysis powered by Perkly · Refresh anytime for updated insights</p>
                </div>
              )}
            </div>
            )}

            {/* VERIFY CLAIM TAB */}

            {/* ANALYTICS TAB */}
            {dashTab === 'analytics' && (
            <div data-testid="analytics-panel">
              {analyticsLoading || !analytics ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total Views', value: analytics.overview.total_views, color: '#8A9A8B' },
                    { label: 'Claims', value: analytics.overview.total_claims, color: '#C8A97E' },
                    { label: 'Redemptions', value: analytics.overview.total_redemptions, color: '#5DBB63' },
                    { label: 'Claim Rate', value: `${analytics.overview.claim_rate}%`, color: '#A07BC8' },
                  ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl p-4 border border-[#8A9A8B]/10 text-center">
                      <p className="text-xs text-[#2B2B2B]/50 mb-1">{s.label}</p>
                      <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Claims last 7 days */}
                <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10 mb-6">
                  <h3 className="font-bold text-[#2B2B2B] mb-4 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>Claims Last 7 Days</h3>
                  <div className="flex items-end gap-2 h-20">
                    {analytics.claims_by_day.map(d => {
                      const max = Math.max(...analytics.claims_by_day.map(x => x.claims), 1);
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                          <span className="text-xs text-[#C8A97E] font-bold opacity-0 group-hover:opacity-100 transition-opacity">{d.claims}</span>
                          <div className="w-full rounded-t-lg" style={{ height: `${Math.max((d.claims / max) * 100, 4)}%`, background: d.claims > 0 ? '#C8A97E' : '#8A9A8B20', minHeight: '4px' }} />
                          <span className="text-xs text-[#2B2B2B]/40">{d.day}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Deal performance */}
                <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10 mb-6">
                  <h3 className="font-bold text-[#2B2B2B] mb-4 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>Deal Performance</h3>
                  <div className="space-y-3">
                    {analytics.deal_performance.map((d, i) => (
                      <div key={d.deal_id} className="flex items-center gap-3">
                        <span className="text-sm font-bold text-[#C8A97E]/60 w-5">#{i+1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[#2B2B2B] truncate">{d.title}</p>
                          <p className="text-xs text-[#8A9A8B]">{d.views} views · {d.claims} claims · {d.redemptions} redeemed</p>
                        </div>
                        <span className="text-sm font-bold text-[#C8A97E]">{d.conversion}%</span>
                      </div>
                    ))}
                    {analytics.deal_performance.length === 0 && <p className="text-xs text-[#2B2B2B]/40 text-center py-4">No deals yet. Add your first deal to see performance data.</p>}
                  </div>
                </div>

                {/* AI Suggestions */}
                <div className="bg-[#2B2B2B] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={16} className="text-[#C8A97E]" />
                    <h3 className="font-bold text-white text-sm">Perkly Recommendations</h3>
                  </div>
                  <div className="space-y-3">
                    {analytics.suggestions.map((s) => (
                      <div key={s} className="flex items-start gap-2 text-sm text-white/70 leading-relaxed">
                        <span className="text-[#C8A97E] mt-0.5 flex-shrink-0">→</span>
                        {s}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Map Performance */}
                {(analytics.overview.map_views > 0 || analytics.overview.marker_clicks > 0 || analytics.overview.direction_requests > 0) && (
                <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10 mt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin size={16} className="text-[#C8A97E]" />
                    <h3 className="font-bold text-[#2B2B2B] text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>Map Performance</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Map Views', value: analytics.overview.map_views, color: '#C8A97E' },
                      { label: 'Marker Clicks', value: analytics.overview.marker_clicks, color: '#6B7CFF' },
                      { label: 'Directions', value: analytics.overview.direction_requests, color: '#5DBB63' },
                      { label: 'Promoted Deals', value: analytics.overview.promoted_deals, color: '#C07CFF' },
                    ].map(s => (
                      <div key={s.label} className="text-center">
                        <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
                        <p className="text-xs text-[#2B2B2B]/50 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {analytics.overview.marker_clicks > 0 && (
                    <p className="text-xs text-[#2B2B2B]/45 mt-4 text-center">
                      Map conversion: {Math.round((analytics.overview.marker_clicks / Math.max(analytics.overview.map_views, 1)) * 100)}% of map views click through
                    </p>
                  )}
                </div>
                )}
              </>
              )}
            </div>
            )}

            {/* GROWTH NETWORK TAB */}
            {dashTab === 'growth' && (
              <div data-testid="growth-tab-panel">
                <GrowthNetworkPage />
              </div>
            )}

            {/* STORE HOURS TAB */}
            {dashTab === 'hours' && (
              <div data-testid="store-hours-tab-panel">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Store Hours</h2>
                    <p className="text-xs text-[#2B2B2B]/50 mt-0.5">Set your weekly hours. Students will see these when viewing your deals.</p>
                  </div>
                  <button
                    onClick={saveHours}
                    disabled={hoursSaving}
                    data-testid="save-store-hours-btn"
                    className="btn-primary text-sm px-5 py-2.5"
                  >
                    {hoursSaving ? 'Saving…' : 'Save Hours'}
                  </button>
                </div>

                {hoursMsg && (
                  <div className={`flex items-center gap-2 text-sm rounded-xl p-3 mb-4 ${hoursMsg.includes('Failed') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
                    <CheckCircle size={15} /> {hoursMsg}
                  </div>
                )}

                {hoursLoading ? (
                  <div className="text-center py-10 text-[#2B2B2B]/40 text-sm">Loading hours…</div>
                ) : (
                  <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 overflow-hidden" data-testid="store-hours-editor">
                    {Object.entries(DAY_LABELS).map(([day, label], i) => {
                      const h = storeHours[day] || { open: '', close: '', closed: false };
                      const isWeekend = day === 'saturday' || day === 'sunday';
                      return (
                        <div
                          key={day}
                          className={`flex items-center gap-4 px-5 py-3.5 ${i < 6 ? 'border-b border-[#8A9A8B]/8' : ''} ${isWeekend ? 'bg-[#F6F1E9]/40' : ''}`}
                          data-testid={`hours-row-${day}`}
                        >
                          <span className="w-24 text-sm font-semibold text-[#2B2B2B]">{label}</span>

                          <button
                            type="button"
                            onClick={() => setStoreHours(prev => ({ ...prev, [day]: { ...prev[day], closed: !prev[day].closed } }))}
                            data-testid={`hours-toggle-${day}`}
                            className="flex items-center gap-1.5 text-xs font-semibold min-w-[80px]"
                          >
                            {h.closed
                              ? <><span className="text-red-400">Closed</span></>
                              : <><span className="text-[#8A9A8B]">Open</span></>
                            }
                            <span className={`w-8 h-4 rounded-full transition-colors flex items-center px-0.5 ${h.closed ? 'bg-[#2B2B2B]/15' : 'bg-[#C8A97E]'}`}>
                              <span className={`w-3 h-3 rounded-full bg-white shadow transition-transform ${h.closed ? '' : 'translate-x-4'}`} />
                            </span>
                          </button>

                          {!h.closed && (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="time"
                                value={h.open || ''}
                                onChange={e => setStoreHours(prev => ({ ...prev, [day]: { ...prev[day], open: e.target.value } }))}
                                data-testid={`hours-open-${day}`}
                                className="border border-[#8A9A8B]/20 rounded-lg px-2 py-1.5 text-xs text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E] w-[110px]"
                              />
                              <span className="text-[#2B2B2B]/30 text-xs font-semibold">to</span>
                              <input
                                type="time"
                                value={h.close || ''}
                                onChange={e => setStoreHours(prev => ({ ...prev, [day]: { ...prev[day], close: e.target.value } }))}
                                data-testid={`hours-close-${day}`}
                                className="border border-[#8A9A8B]/20 rounded-lg px-2 py-1.5 text-xs text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E] w-[110px]"
                              />
                            </div>
                          )}
                          {h.closed && <span className="text-xs text-[#2B2B2B]/30 italic">Not open this day</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-[#2B2B2B]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-xl" data-testid="delete-confirm-modal">
              <h3 className="font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Delete Deal?</h3>
              <p className="text-sm text-[#2B2B2B]/60 mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 btn-secondary py-2.5 text-sm justify-center">Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors" data-testid="confirm-delete-button">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Deal Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-[#2B2B2B]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-3xl w-full shadow-xl flex flex-col"
              style={{ maxWidth: '640px', maxHeight: '92vh' }}
              data-testid="deal-form-modal"
            >
              {/* Sticky Header */}
              <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-[#8A9A8B]/12 flex-shrink-0">
                <h3 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {editingDeal ? 'Edit Deal' : 'Create New Deal'}
                </h3>
                <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full bg-[#F6F1E9] flex items-center justify-center text-[#2B2B2B]/60 hover:text-[#2B2B2B] text-lg leading-none">×</button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 px-7 py-5">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl p-3 mb-4">{formError}</div>
                )}

                <form onSubmit={handleFormSubmit} id="deal-form" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Deal Title *</label>
                      <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. $5 Burger Tuesdays" className="perkly-input" data-testid="deal-title-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Description *</label>
                      <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Describe the deal in detail..." className="perkly-input resize-none" data-testid="deal-description-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Category *</label>
                      <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="perkly-input">
                        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">City *</label>
                      <select value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value })} className="perkly-input">
                        {cities.filter(c => c.is_active).map((c) => <option key={c.city_id} value={c.city_id}>{c.full_name || `${c.name}, ${c.state}`}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Deal Price ($)</label>
                      <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. 5.00 (leave blank if N/A)" className="perkly-input" data-testid="deal-price-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Original Price ($)</label>
                      <input type="number" step="0.01" min="0" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} placeholder="e.g. 12.00" className="perkly-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Discount Label</label>
                      <input value={form.discount_text} onChange={(e) => setForm({ ...form, discount_text: e.target.value })} placeholder="e.g. 50% Off / Buy 1 Get 1" className="perkly-input" data-testid="deal-discount-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Start Date</label>
                      <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="perkly-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">End Date</label>
                      <input
                        type="date"
                        value={subscription ? subscription.end_date : form.end_date}
                        onChange={(e) => !subscription && setForm({ ...form, end_date: e.target.value })}
                        className={`perkly-input ${subscription ? 'opacity-60 cursor-not-allowed bg-[#F6F1E9]' : ''}`}
                        readOnly={!!subscription}
                      />
                      {subscription && (
                        <p className="text-xs text-[#C8A97E] mt-1">Locked to your subscription end date</p>
                      )}
                    </div>

                    {/* Image Upload */}
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Deal Image *</label>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="deal-image-file-input" />
                      <div
                        onClick={() => !imageUploading && fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-2xl transition-all cursor-pointer ${imageUploading ? 'opacity-60 cursor-wait' : 'hover:border-[#C8A97E]'} ${form.image_url ? 'border-[#C8A97E]/50' : 'border-[#8A9A8B]/30'}`}
                        data-testid="image-upload-zone"
                      >
                        {form.image_url ? (
                          <div className="relative p-2">
                            <img src={form.image_url} alt="preview" className="w-full h-32 object-cover rounded-xl" onError={(e) => { e.target.style.display='none'; }} />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setForm((f) => ({ ...f, image_url: '' })); }}
                              className="absolute top-4 right-4 bg-[#2B2B2B]/70 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-500 transition-colors"
                            >×</button>
                            <p className="text-xs text-center text-[#8A9A8B] mt-2 pb-1">Click to change image</p>
                          </div>
                        ) : (
                          <div className="py-8 text-center">
                            {imageUploading ? (
                              <div className="flex flex-col items-center gap-2 px-4" data-testid="image-upload-progress">
                                <div className="w-8 h-8 border-3 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
                                <p className="text-sm text-[#8A9A8B]">Uploading... {uploadProgress}%</p>
                                <div className="w-full max-w-[160px] h-1.5 bg-[#E8E0D0] rounded-full overflow-hidden">
                                  <div className="h-full bg-[#C8A97E] rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="w-12 h-12 rounded-full bg-[#F6F1E9] flex items-center justify-center mx-auto mb-3">
                                  <Upload size={20} className="text-[#C8A97E]" />
                                </div>
                                <p className="text-sm font-semibold text-[#2B2B2B]">Click to upload image</p>
                                <p className="text-xs text-[#8A9A8B] mt-1">JPG, PNG, WebP — max 15 MB</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Tags (comma separated)</label>
                      <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. student, food, tuesday" className="perkly-input" />
                    </div>

                    <div className="sm:col-span-2 bg-[#F6F1E9] rounded-2xl p-4 border border-[#C8A97E]/15">
                      <p className="text-xs font-bold text-[#8A9A8B] uppercase tracking-wider mb-1">Claim Code Tracking</p>
                      <p className="text-xs text-[#2B2B2B]/55 leading-relaxed">
                        Students who find this deal on Perkly will be able to generate a unique <strong>PKY-XXXXXX</strong> claim code.
                        When they show it to you, verify it in the <strong>Verify Claim</strong> tab to track every customer from Perkly.
                      </p>
                    </div>

                    {/* Deal Visibility / Map Promotion */}
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">Deal Visibility Options</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Standard */}
                        <label className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${!form.is_promoted ? 'border-[#2B2B2B] bg-[#2B2B2B]/5' : 'border-[#8A9A8B]/20 hover:border-[#8A9A8B]/40'}`} data-testid="visibility-standard">
                          <input type="radio" name="visibility" checked={!form.is_promoted} onChange={() => setForm({...form, is_promoted: false})} className="hidden" />
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#8A9A8B]/12 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <List size={14} className="text-[#8A9A8B]" />
                            </div>
                            <div>
                              <p className="font-bold text-sm text-[#2B2B2B]">Standard Listing</p>
                              <p className="text-xs text-[#2B2B2B]/50 mt-0.5 leading-relaxed">Category pages · Search results · Standard map pin</p>
                            </div>
                          </div>
                        </label>

                        {/* Promote on Map */}
                        <label className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${form.is_promoted ? 'border-[#C8A97E] bg-[#C8A97E]/8' : promoStatus.can_promote || (editingDeal?.is_promoted) ? 'border-[#8A9A8B]/20 hover:border-[#C8A97E]/50' : 'border-[#8A9A8B]/15 opacity-50 cursor-not-allowed'}`} data-testid="visibility-promoted">
                          <input type="radio" name="visibility" checked={form.is_promoted} onChange={() => (promoStatus.can_promote || editingDeal?.is_promoted) && setForm({...form, is_promoted: true})} className="hidden" disabled={!promoStatus.can_promote && !editingDeal?.is_promoted} />
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#C8A97E]/15 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <MapPin size={14} className="text-[#C8A97E]" />
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <p className="font-bold text-sm text-[#2B2B2B]">Promote on Map</p>
                                <span className="text-xs bg-[#C8A97E]/15 text-[#C8A97E] font-bold px-1.5 py-0.5 rounded-full">★</span>
                              </div>
                              <p className="text-xs text-[#2B2B2B]/50 leading-relaxed">Featured marker · Business logo · Priority discovery</p>
                              {promoStatus.max_promoted > 0 && (
                                <p className="text-xs text-[#C8A97E] mt-1 font-semibold">
                                  {promoStatus.slots_remaining} slot{promoStatus.slots_remaining !== 1 ? 's' : ''} available
                                </p>
                              )}
                            </div>
                          </div>
                        </label>
                      </div>
                      {promoStatus.max_promoted === 0 && (
                        <p className="text-xs text-[#8A9A8B] mt-2">
                          Map promotion is available on paid plans. <Link to="/pricing" className="text-[#C8A97E] underline">View plans →</Link>
                        </p>
                      )}
                      {promoStatus.max_promoted > 0 && !promoStatus.can_promote && !editingDeal?.is_promoted && (
                        <p className="text-xs text-orange-500 mt-2">
                          All {promoStatus.max_promoted} promotion slots are in use. Deactivate a promoted deal to promote this one.
                        </p>
                      )}
                    </div>
                  </div>
                </form>
              </div>

              {/* Sticky Footer */}
              <div className="px-7 py-5 border-t border-[#8A9A8B]/12 flex gap-3 flex-shrink-0 bg-white rounded-b-3xl">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 btn-secondary text-sm py-3 justify-center">Cancel</button>
                <button form="deal-form" type="submit" disabled={formLoading || imageUploading} className="flex-1 btn-primary text-sm py-3 justify-center disabled:opacity-60" data-testid="deal-form-submit">
                  {formLoading ? 'Saving...' : editingDeal ? 'Save Changes' : 'Create Deal'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
